import { c as _c } from "react/compiler-runtime";
import HtmlToReact, { Parser } from 'html-to-react';
import type { CSSProperties, HTMLAttributeReferrerPolicy } from 'react';
import React from 'react';
import type { Breakpoint } from '@mui/material';
import Box from '@mui/material/Box';
import type { ButtonProps } from '@mui/material/Button';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import type { Element } from 'domhandler';
import { omit } from 'es-toolkit/compat';
import Image from 'next/image';
import Link from 'next/link';
import { urlForCmsAsset } from '@/lib/urlForCmsAsset';
import { urlForCmsLink } from '@/lib/urlForCmsLink';
import { cmsHtmlStyles } from '@/styles/cmsHtmlStyles';
type HtmlToReactProcessingInstruction = {
  replaceChildren?: boolean;
  shouldProcessNode: (node: Element) => boolean;
  processNode: (node: Element, children: React.ReactNode[], index: number) => React.ReactNode;
};
export interface CmsHtmlContentProps {
  html?: string | null;
  plain?: boolean;
  rules?: HtmlToReactProcessingInstruction[];
}
export function CmsHtmlContent(t0) {
  const $ = _c(8);
  const {
    html,
    rules,
    plain
  } = t0;
  const t1 = plain ? undefined : cmsHtmlStyles;
  let t2;
  if ($[0] !== html || $[1] !== rules) {
    let t3;
    if ($[3] !== rules) {
      t3 = rules || [];
      $[3] = rules;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = Parser().parseWithInstructions(html?.replace(/>\s+</g, "><")?.trim() || "", _temp, [...t3, ...htmlToReactProcessNodeDefinitions]);
    $[0] = html;
    $[1] = rules;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <Box sx={t1}>{t2}</Box>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp() {
  return true;
}
const buttonClassMapping: Record<string, Partial<ButtonProps>> = {
  outline: {
    variant: 'outlined'
  },
  'btn-primary': {
    color: 'primary'
  },
  'btn-secondary': {
    color: 'secondary'
  },
  'btn-large': {
    size: 'large'
  },
  'btn-small': {
    size: 'small'
  }
};
function getButtonPropsFromClassNames(classNames: string) {
  const classes = classNames.split(' ');
  const result: Partial<ButtonProps> = {};
  if (classNames.includes('btn-white')) {
    result.sx = {
      backgroundColor: classNames.includes('outline') ? undefined : '#fff',
      borderColor: '#fff',
      color: '#fff',
      '&:hover, &:active, &:focus': {
        color: '#000',
        backgroundColor: '#f0f0f0',
        borderColor: '#f0f0f0'
      }
    };
  }
  return classes.reduce((props, className) => ({
    ...props,
    ...buttonClassMapping[className]
  }), result);
}
const htmlToReactProcessNodeDefinitions: HtmlToReactProcessingInstruction[] = [{
  shouldProcessNode: node => node.name === 'a',
  processNode: (node, children, index) => {
    if (!node.attribs.href || node.attribs.class?.includes('btn')) {
      const buttonProps = getButtonPropsFromClassNames(node.attribs.class || '');
      if (!buttonProps.variant) {
        buttonProps.variant = 'contained';
      }
      return <Button key={index} href={urlForCmsLink(node.attribs.href)} {...buttonProps}>
                        {children[0]}
                    </Button>;
    }
    return <Link key={index} href={urlForCmsLink(node.attribs.href)} className={node.attribs.class} target={node.attribs.target}>
                    {children[0]}
                </Link>;
  }
}, {
  shouldProcessNode: node => node.name === 'iframe',
  processNode: (node: Element) => {
    const {
      referrerpolicy,
      allowfullscreen,
      style,
      ...props
    } = omit(node.attribs || {}, ['frameborder']);
    return <iframe {...props} style={convertStyleToJSX(style)} referrerPolicy={(referrerpolicy || 'no-referrer') as HTMLAttributeReferrerPolicy} allowFullScreen={typeof allowfullscreen !== 'undefined'} />;
  }
}, {
  shouldProcessNode: node => node.name === 'img',
  processNode: (node, children, index) => node.attribs.src ? <Image key={index} src={urlForCmsAsset(node.attribs.src)} className={node.attribs.class} width={node.attribs.width ? parseInt(node.attribs.width) : undefined} height={node.attribs.height ? parseInt(node.attribs.height) : undefined} alt={node.attribs.alt} title={node.attribs.title} /> : null
}, {
  shouldProcessNode: node => node.attribs?.class?.includes('grid-'),
  processNode: (node, children, index) => {
    const classList = node.attribs.class.split(' ').map(c => c.trim()).filter(c => c);
    const isContainer = classList.includes('grid-container');
    if (isContainer) {
      const sizeClass = classList.find(c => c.startsWith('grid-spacing-'));
      const sizeValue = sizeClass ? parseInt(sizeClass.replace('grid-spacing-', ''), 10) : 8;
      return <Grid container spacing={2} key={index} className={node.attribs.class} columnSpacing={sizeValue}>
                        {children}
                    </Grid>;
    }
    const gridProps: {
      size: Partial<Record<Breakpoint, number>>;
    } = {
      size: {}
    };
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'] as Breakpoint[];
    for (const breakpoint of breakpoints) {
      const sizeClass = classList.find(c => c.startsWith(`grid-${breakpoint}-`));
      if (sizeClass) {
        const sizeValue = parseInt(sizeClass.replace(`grid-${breakpoint}-`, ''), 10);
        if (sizeValue && !isNaN(sizeValue)) {
          gridProps.size[breakpoint] = sizeValue;
        }
      }
    }
    return <Grid {...gridProps} key={index} className={node.attribs.class}>
                    {children}
                </Grid>;
  }
}, {
  shouldProcessNode: () => true,
  processNode: HtmlToReact.ProcessNodeDefinitions().processDefaultNode
}];
function convertStyleToJSX(style?: string) {
  if (!style) {
    return undefined;
  }
  return style.split(';').reduce((styleObject, line) => {
    const [property, value] = line.split(':').map(part => part.trim());
    if (!property || !value) {
      return styleObject;
    }
    const camelCaseProperty = property.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
    return {
      ...styleObject,
      [camelCaseProperty]: value
    } as CSSProperties;
  }, {} as CSSProperties);
}